import { z } from 'zod';
import { CalendarDateSchema, IdSchema, MoneySchema } from './common.schema';

/**
 * The period a report covers. `all` is every day the lab has a record of: the
 * default on every screen since the lab's request of 17 October 2026, item 4.
 */
export const ReportRangeSchema = z.enum(['all', 'today', 'week', 'month']);

/**
 * `GET /reports/summary`. Active, due, overdue, on time percentage, average
 * turnaround and completed, plus a sparkline series and a delta against the
 * previous period.
 */
export const ReportSummarySchema = z.object({
  range: ReportRangeSchema,
  active: z.number().int().nonnegative(),
  due: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  onTimePercent: z.number().min(0).max(100),
  averageTurnaroundDays: z.number().nonnegative(),
  completed: z.number().int().nonnegative(),
  sparkline: z.array(z.number()),
  previous: z.object({
    active: z.number().int().nonnegative(),
    due: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    onTimePercent: z.number().min(0).max(100),
    averageTurnaroundDays: z.number().nonnegative(),
    completed: z.number().int().nonnegative(),
  }),
});

export const SalesRollupRowSchema = z.object({
  key: z.string(),
  units: z.number().int().nonnegative(),
  value: MoneySchema,
  orders: z.number().int().nonnegative(),
});

/**
 * `GET /reports/sales`. Units sold and best selling item are the two metrics
 * the client named as most important, so they are top level fields rather than
 * something the client has to derive from a rollup.
 *
 * Rollups are sorted by units descending. Refused to `tech`, section 7.10 item 3.
 */
export const SalesReportSchema = z.object({
  range: ReportRangeSchema,
  unitsSold: z.number().int().nonnegative(),
  previousUnitsSold: z.number().int().nonnegative(),
  bestSeller: z.string().nullable(),
  byItem: z.array(SalesRollupRowSchema),
  byCategory: z.array(SalesRollupRowSchema),
  byDentist: z.array(SalesRollupRowSchema),
  /**
   * Keyed by the origin's key, `local`, `international` or `online`, which the
   * screen labels as it labels a stage. In place of `byCountry`, section 1 of the
   * request of 24 September 2026. (The API still sends `byCountry` as well, keyed
   * by the label, for a device on the build before; nothing here reads it.)
   */
  byOrigin: z.array(SalesRollupRowSchema),
});

/**
 * `GET /reports/staff`. Requires the `staff` view, or `perf` for the caller's
 * own row alone. No role is refused as such: the lab's answer of
 * 11 October 2026 was that the tick always wins.
 */
export const StaffProductivityRowSchema = z.object({
  userId: IdSchema,
  name: z.string(),
  /**
   * The CASES this person finished work on in the range: a case worked twice,
   * at two stages or because it came back to them, is one. The lab's request
   * of 17 October 2026, item 6. The name is from when it counted tasks.
   */
  tasksCompleted: z.number().int().nonnegative(),
  activeSeconds: z.number().int().nonnegative(),
  averageSecondsPerTask: z.number().nonnegative(),
  onTimePercent: z.number().min(0).max(100),
  utilisationPercent: z.number().min(0).max(100),
  /**
   * The units on those cases, each case once: a three unit bridge is three, a
   * full arch as many teeth as it covers. Request of 3 October 2026, section 4,
   * as item 6 of the request of 17 October 2026 revised it.
   */
  teethWorked: z.number().int().nonnegative(),
  /** The job orders this person created in the range. */
  jobOrdersCreated: z.number().int().nonnegative(),
  /**
   * Whether the bench figures are theirs: a bench role, or anyone who finished
   * a piece of work in the range. False for somebody on the report only for
   * the job orders they took in, whose tasks and time read as not applicable.
   */
  benchWork: z.boolean(),
});

/**
 * The staff report also answers an exact period, `?from=&to=` in lab days, which
 * it echoes as `custom` with the two days. Request of 4 October 2026.
 */
export const StaffRangeSchema = z.enum(['all', 'today', 'week', 'month', 'custom']);

export const StaffReportSchema = z.object({
  range: StaffRangeSchema,
  from: CalendarDateSchema.optional(),
  to: CalendarDateSchema.optional(),
  rows: z.array(StaffProductivityRowSchema),
});

export type ReportRange = z.infer<typeof ReportRangeSchema>;
export type SalesReport = z.infer<typeof SalesReportSchema>;
export type StaffReport = z.infer<typeof StaffReportSchema>;
